import React, { useCallback, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';
import omit from 'lodash/omit';

import {
  addressValidator,
  emailValidator,
  required,
  compose,
  tooShort,
  validatePassword,
} from '@front/common/src/forms/validations';
import { getDateInputConfig } from '@front/common/src/forms/validators/date';
import type { Address, Salutation } from '@front/common/src/types/commons';
import { isCountryFR } from '@front/common/src/utils/country';

import { useValidateUniqueUser } from '@front/pro/src/queries/account-queries';
import { UserInformation as UserInformationType } from '@front/pro/src/signup/types';
import { useUniverse, useUserInformation } from '@front/pro/src/signup/signup-form-context';
import { proBirthDateValidator } from '@front/pro/src/signup/validation';

import { translate } from '@front/i18n';

import Button from '@front/web-ui/src/components/button/button';
import GenderSelector from '@front/web-ui/src/components/gender-selector/gender-selector';
import { updateAddress } from '@front/web-ui/src/forms/mutators';
import { InputAdapter } from '@front/web-ui/src/forms/adapters';
import InputPhone from '@front/web-ui/src/components/input-phone/input-phone';
import { WoosmapAutocomplete } from '@front/web-ui/src/components/autocomplete-address/woosmap-autocomplete';
import { ValidatedPassword } from '@front/web-ui/src/components/input-password/validated-password';

import { Screen } from '../../components/screen/screen';
import ButtonBar from '../../components/button-bar/button-bar';
import { getRoute } from '../../routes/routes';
import { useNavigateHistoryWithQuery } from '../../hooks/use-navigate-history-with-query';
import { logSignupStepEvent, setUserProperties } from '../../utils/tracking';
import { useWebStepper } from '../../hooks/use-web-stepper';
import { useRedirectionStep } from '../../hooks/use-redirection-step';
import { useShouldShowRevenueSimulator } from '../../hooks/use-show-revenue-simulator';

import AccountAlreadyExistsAlert from './account-already-exists-alert';

const t = translate('screens.user_information');

type UserFormInformationType = Omit<UserInformationType, 'country_code'> & {
  mobileFormatted?: string;
  isMobileValid?: boolean;
};

const formValuesToUserInformationType = (values: UserFormInformationType): UserInformationType => {
  const mobile = values.mobileFormatted;
  return {
    mobile: mobile!,
    country_code: isCountryFR() ? 'FR' : 'GB',
    ...omit(values, ['mobile', 'mobileFormatted', 'isMobileValid']),
  };
};

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

// I have to do this for this screen beacuse margins are not collapsing and I don't understand why
// So here, margins-top should be 20px and should collapse with the margin-bottom of the last field (15px),
// resulting in a spacing of 20px (because larger margins wins)
// but it is not the case
const StyledButtonBar = styled(ButtonBar)`
  margin-top: 5px;
`;

const { mask: dateInputMask, mode, type } = getDateInputConfig();

const handlePhoneValidation = ({ mobile, isMobileValid }: { mobile: string; isMobileValid?: boolean }) => {
  if (!mobile) return t('inputs.phone.errors.required');
  if (!isMobileValid) return t('inputs.phone.errors.invalid');
  return undefined;
};

type MobileStateValue = {
  isMobileValid?: boolean;
  mobileFormatted?: string;
  phoneFlag?: string;
};

const validate = (values: Omit<UserInformationType, 'country_code'> & Address & MobileStateValue) => ({
  address: addressValidator('address')(values),
  mobile: handlePhoneValidation({ mobile: values.mobile, isMobileValid: values.isMobileValid }),
});

const UserInformation = () => {
  useRedirectionStep();
  const { historyPushWithQuery } = useNavigateHistoryWithQuery();
  const { nextStep, currentStep } = useWebStepper();
  const { userInformation, setUserInformation } = useUserInformation();
  const [addressError, setAddressError] = useState<string>();
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const shouldShowRevenueSimulator = useShouldShowRevenueSimulator();
  const { validateUniqueUser, validateUniqueUserError } = useValidateUniqueUser();
  const { universe } = useUniverse();

  useEffect(() => {
    // In some browser, focus on the input is not enough to view the title of the page so we are scrolling to the top
    // eslint-disable-next-line no-multi-assign
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    logSignupStepEvent({ step: currentStep, universe });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = useCallback(
    (values: UserFormInformationType) => {
      const mappedValues = formValuesToUserInformationType(values);
      validateUniqueUser(
        { email: values.email, phone: mappedValues.mobile },
        {
          onSuccess: () => {
            // Order matters here, since if userInformations is not stored,
            // UseSignupStepper will not allow to go to next step
            setUserInformation(mappedValues);
            setUserProperties({ country_code: mappedValues.country_code });
            logSignupStepEvent({ step: nextStep, universe });
            historyPushWithQuery(getRoute(nextStep));
          },
        },
      );
      //
    },
    [validateUniqueUser, setUserInformation, nextStep, historyPushWithQuery, universe],
  );

  const handlePhoneFieldBlur =
    ({ mobile, isMobileValid }: { mobile: string; isMobileValid?: boolean }) =>
    () => {
      const errorKey = handlePhoneValidation({ mobile, isMobileValid });
      return setPhoneError(errorKey);
    };

  return (
    <Screen title={t('.title')} isCenteredOnDesktop={false}>
      <AccountAlreadyExistsAlert isOpen={!!validateUniqueUserError} />
      <Form
        onSubmit={onSubmit}
        validate={validate}
        mutators={{
          updatePhone: ([{ isValid, value, formattedValue, flag }], state, utils) => {
            // clear phone error if any
            setPhoneError(undefined);
            utils.changeValue(state, 'mobile', () => value);
            utils.changeValue(state, 'mobileFormatted', () => formattedValue);
            utils.changeValue(state, 'isMobileValid', () => isValid);
            utils.changeValue(state, 'phoneFlag', () => flag);
          },
          updateAddress,
        }}
        initialValues={userInformation}
      >
        {({ handleSubmit, form, values }) => (
          <StyledForm onSubmit={handleSubmit} id='form'>
            <Field<Salutation> name='salutation' validate={required('salutation')}>
              {({ input, meta }) => (
                <GenderSelector
                  value={input.value}
                  onChange={input.onChange}
                  error={meta.touched && meta.error ? meta.error : undefined}
                />
              )}
            </Field>
            <Field
              component={InputAdapter}
              validate={compose(required('first_name'), tooShort('first_name', 1))}
              name='first_name'
              type='text'
              label={t('inputs.first_name.placeholder')}
              id='formSignupFirstName'
              autoComplete='given-name'
            />
            <Field
              component={InputAdapter}
              validate={compose(required('last_name'), tooShort('last_name', 1))}
              name='last_name'
              type='text'
              label={t('inputs.last_name.placeholder')}
              id='formSignupLastName'
              autoComplete='family-name'
            />
            <Field
              component={InputAdapter}
              validate={compose(required('email'), emailValidator('email'))}
              name='email'
              type='email'
              label={t('inputs.email.placeholder')}
              id='formSignupEmail'
              autoComplete='email'
            />
            <Field
              component={ValidatedPassword}
              validate={compose(required('password'), validatePassword('password'))}
              name='password'
              label={t('inputs.password.placeholder')}
              id='formSignUpPassword'
              autoComplete='password'
            />
            <Field name='mobile'>
              {({ input, meta }) => (
                <InputPhone
                  defaultCountry={values?.phoneFlag}
                  name='mobile'
                  label={t('inputs.phone.placeholder')}
                  id='mobile'
                  autoComplete='tel'
                  onChange={form.mutators.updatePhone}
                  value={input.value}
                  onBlur={handlePhoneFieldBlur({ mobile: values.mobile, isMobileValid: values.isMobileValid })}
                  onFocus={input.onFocus}
                  error={(meta.touched && meta.error) || phoneError}
                />
              )}
            </Field>
            <Field name='address'>
              {({ meta }) => (
                <WoosmapAutocomplete
                  id='formSignupAddress'
                  name='formSignupAddress'
                  formattedAddress={values.address}
                  setAddressFields={form.mutators.updateAddress}
                  error={(meta.touched && meta.error) || addressError}
                  setAddressError={setAddressError}
                  apiKey={process.env.REACT_APP_WOOSMAP_API_KEY!}
                />
              )}
            </Field>
            <Field
              component={InputAdapter}
              name='date_of_birth'
              validate={compose(required('birthday'), proBirthDateValidator('birthday'))}
              label={t('inputs.birthday.placeholder')}
              id='date_of_birth'
              type={type}
              inputMode={mode}
              mask={dateInputMask}
              placeholder='30/01/1960'
            />
            <StyledButtonBar>
              <Button size='lg' type='submit'>
                {shouldShowRevenueSimulator ? t('base.next') : t('.cta_text')}
              </Button>
            </StyledButtonBar>
          </StyledForm>
        )}
      </Form>
    </Screen>
  );
};

export default UserInformation;
